import { type WorkoutFormat } from "api-client/legacy-trpc";
import { canWorkoutFormatHaveSupersets } from "utils";

import {
  type WorkoutEditStore,
  type WorkoutItemData,
} from "src/workouts/WorkoutEditStore";

export function isSection(item: WorkoutItemData): boolean {
  return item.type === "GROUP" && item.groupType !== "SUPER_SET";
}

export function isSuperset(item: WorkoutItemData): boolean {
  return item.type === "GROUP" && item.groupType === "SUPER_SET";
}

export function canHaveSupersets(format: WorkoutFormat | null): boolean {
  return canWorkoutFormatHaveSupersets(format);
}

export function isDescendantOf(
  itemId: string,
  potentialAncestorId: string,
  store: WorkoutEditStore,
): boolean {
  const item = store.getWorkoutItemById(itemId);

  if (!item) {
    return false;
  }

  let currentParentId = item.parentId;

  while (currentParentId !== null) {
    if (currentParentId === potentialAncestorId) {
      return true;
    }

    const parent = store.getWorkoutItemById(currentParentId);

    if (!parent) {
      break;
    }

    currentParentId = parent.parentId;
  }

  return false;
}

export type MinimapInstruction =
  | "reorder-above"
  | "reorder-below"
  | "make-child";

interface DropValidation {
  allowed: boolean;
  instruction: MinimapInstruction | null;
}

export function canDropOnTarget(
  sourceId: string,
  targetId: string,
  instruction: MinimapInstruction,
  store: WorkoutEditStore,
): DropValidation {
  if (sourceId === targetId) {
    return { allowed: false, instruction: null };
  }

  const source = store.getWorkoutItemById(sourceId);
  const target = store.getWorkoutItemById(targetId);

  if (!source || !target) {
    return { allowed: false, instruction: null };
  }

  if (isDescendantOf(targetId, sourceId, store)) {
    return { allowed: false, instruction: null };
  }

  const newParentId =
    instruction === "make-child" ? target.id : target.parentId;
  const newParent =
    newParentId !== null ? store.getWorkoutItemById(newParentId) : null;

  if (isSection(source) && newParentId !== null) {
    return { allowed: false, instruction: null };
  }

  if (isSuperset(source) && newParent && isSuperset(newParent)) {
    return { allowed: false, instruction: null };
  }

  if (
    isSuperset(source) &&
    newParent &&
    newParent.type === "GROUP" &&
    !canHaveSupersets(newParent.format)
  ) {
    return { allowed: false, instruction: null };
  }

  return { allowed: true, instruction };
}
